// c:/xampp/htdocs/miners-market-main/components/forms/ConsumablesForm.js

import React from 'react';
import AccordionSection from '../AccordionSection.jsx';
import MediaManager from './MediaManager.jsx';

const PPE_TYPES = ['Boots', 'Pants', 'Jackets', 'Socks', 'Gloves', 'Other'];

const SIZE_CONFIG = {
    'Pants': { type: 'standard', sizes: ['28', '30', '32', '34', '36', '38', '40', '42', '44', '46', '48', '50'] },
    'Jackets': { type: 'standard', sizes: ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'] },
    'Gloves': { type: 'standard', sizes: ['XS', 'S', 'M', 'L', 'XL'] },
    'Boots': { type: 'gendered', sizes: { 'Men': Array.from({length: 14}, (_, i) => (i + 2).toString()), 'Women': Array.from({length: 7}, (_, i) => (i + 2).toString()) } },
    'Socks': { type: 'gendered', sizes: { 'Men': Array.from({length: 14}, (_, i) => (i + 2).toString()), 'Women': Array.from({length: 7}, (_, i) => (i + 2).toString()) } }
};

const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const stringValue = value.toString().replace(/[^0-9.]/g, '');
    const parts = stringValue.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (parts.length > 1) {
        return parts[0] + '.' + parts[1].substring(0, 2);
    }
    return parts[0] + (value.toString().endsWith('.') ? '.' : '');
};

function ConsumablesForm({ formData, onFormChange, openSection, onToggleSection, categories }) {
    const [ppeType, setPpeType] = React.useState('Other');
    const [gender, setGender] = React.useState('Men');
    const [sizeStock, setSizeStock] = React.useState({});
    
    // --- NEW: Local state for "Other" type inputs ---
    const [otherSizeName, setOtherSizeName] = React.useState('');
    const [otherSizeQty, setOtherSizeQty] = React.useState('');

    // --- NEW: Ref to track initialization ---
    const initializedRef = React.useRef(false);

    // --- NEW: Initialize ppeType from category_id on mount ---
    React.useEffect(() => {
        if (!initializedRef.current && formData.category_id && categories) {
            const cat = categories.find(c => c.id == formData.category_id);
            if (cat) {
                // Check if the category name matches one of our PPE types (e.g., "Boots")
                if (PPE_TYPES.includes(cat.name)) {
                    setPpeType(cat.name);
                }
            }
            initializedRef.current = true;
        }
    }, [categories, formData.category_id]);

    // Initialize state from existing formData if editing
    React.useEffect(() => {
        if (formData.size) {
            if (formData.size.startsWith('{')) {
                try {
                    const parsedSizes = JSON.parse(formData.size);
                    setSizeStock(parsedSizes);
                    // If we have a single key in the JSON, it might be an "Other" type
                    const keys = Object.keys(parsedSizes);
                    if (keys.length === 1 && !PPE_TYPES.includes(ppeType)) {
                        setOtherSizeName(keys[0]);
                        setOtherSizeQty(parsedSizes[keys[0]]);
                    }
                } catch (e) {
                    // Error parsing JSON
                }
            } else {
                // Legacy simple string size - treat as "Other"
                setOtherSizeName(formData.size);
                setOtherSizeQty(formatNumberWithCommas(formData.quantity ?? ''));
                // Don't force 'Other' here if we already set it via category_id above
                if (!initializedRef.current) setPpeType('Other');
            }
        }
    }, []);

    // --- NEW: Effect to sync "Other" inputs to sizeStock ---
    React.useEffect(() => {
        if (ppeType === 'Other') {
            if (otherSizeName && otherSizeQty) {
                setSizeStock({ [otherSizeName]: otherSizeQty });
            } else {
                setSizeStock({});
            }
        }
    }, [otherSizeName, otherSizeQty, ppeType]);

    // --- NEW: Automatically update category_id when ppeType changes ---
    React.useEffect(() => {
        if (ppeType && ppeType !== 'Other' && categories) {
            const slug = ppeType.toLowerCase();
            const cat = categories.find(c => c.slug === slug);
            // Only update if we found a matching category and it's different from current
            if (cat && formData.category_id !== cat.id) {
                onFormChange({ target: { name: 'category_id', value: cat.id } });
            }
        }
    }, [ppeType, categories, formData.category_id]);

    // Update formData whenever local stock state changes
    React.useEffect(() => {
        // Calculate total quantity for display/logic, even if not saved to DB column
        const totalQty = Object.values(sizeStock).reduce((a, b) => {
            const clean = b.toString().replace(/,/g, '');
            return a + (parseFloat(clean) || 0);
        }, 0);
        
        // We fake the event object to reuse the existing onFormChange handler
        onFormChange({ target: { name: 'quantity', value: totalQty } });
        // Always save size as JSON string, even for "Other" type. 
        console.log("[ConsumablesForm] onFormChange for size", sizeStock);
        // This ensures quantity is preserved in the size column since the DB lacks a quantity column.
        onFormChange({ target: { name: 'size', value: JSON.stringify(sizeStock) } });
    }, [sizeStock]);

    const handleStockChange = (size, qty) => {
        setSizeStock(prev => {
            const next = { ...prev };
            if (qty !== '') next[size] = qty;
            else delete next[size];
            return next;
        });
    };

    const renderSizeGrid = () => {
        if (ppeType === 'Other') return null;

        const config = SIZE_CONFIG[ppeType];
        let sizesToRender = [];

        if (config.type === 'gendered') {
            sizesToRender = config.sizes[gender];
        } else {
            sizesToRender = config.sizes;
        }

        return (
            <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Manage Stock per Size</h4>
                {config.type === 'gendered' && (
                    <div className="flex gap-4 mb-4">
                        {['Men', 'Women'].map(g => (
                            <label key={g} className="flex items-center space-x-2 cursor-pointer">
                                <input type="radio" name="gender_select" checked={gender === g} onChange={() => setGender(g)} className="text-blue-600 focus:ring-blue-500" />
                                <span className="text-sm text-gray-700">{g}</span>
                            </label>
                        ))}
                    </div>
                )}
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                    {sizesToRender.map(size => (
                        <div key={size} className="flex flex-col">
                            <label className="text-xs text-gray-500 mb-1 text-center">{size}</label>
                            <input type="text" placeholder="0" value={sizeStock[size] ?? ''} onChange={(e) => handleStockChange(size, e.target.value)} className="w-full text-center border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500" />
                        </div>
                    ))}
                </div>
                <div className="mt-3 text-right text-sm text-gray-600">
                    Total Stock: <span className="font-bold text-blue-600">{Object.values(sizeStock).reduce((a, b) => a + (parseFloat(b) || 0), 0)}</span>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-2">
            <AccordionSection title="1. Consumable Details" id="consumableDetails" openSection={openSection} onToggle={onToggleSection}>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Product Name*</label>
                        <input type="text" name="name" value={formData.name || ''} onChange={onFormChange} required className="mt-1 block w-full border-gray-300 rounded-md shadow-sm" placeholder="e.g., Safety Gloves" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Price (R)*</label>
                        <input type="text" name="price" value={formData.price ?? ''} onChange={onFormChange} required className="mt-1 block w-full border-gray-300 rounded-md shadow-sm" />
                    </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Brand</label>
                        <input type="text" name="brand" value={formData.brand || ''} onChange={onFormChange} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Product Type</label>
                        <select value={ppeType} onChange={(e) => { setPpeType(e.target.value); setSizeStock({}); }} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm">
                            {PPE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                </div>

                {/* Dynamic Size Grid or Fallback Input */}
                {ppeType === 'Other' ? (
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Size / Description</label>
                        <input 
                            type="text" 
                            value={otherSizeName} 
                            onChange={(e) => setOtherSizeName(e.target.value)} 
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm" 
                            placeholder="e.g., One Size, Pack of 100" 
                        />
                        <div className="mt-2">
                            <label className="block text-sm font-medium text-gray-700">Total Quantity*</label>
                            <input
                                type="text"
                                value={otherSizeQty ?? ''}
                                onChange={(e) => setOtherSizeQty(e.target.value)}
                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                            />
                        </div>
                    </div>
                ) : (
                    renderSizeGrid()
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea 
                        name="description" 
                        value={formData.description || ''} 
                        onChange={onFormChange} 
                        rows="3" 
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                    ></textarea>
                </div>
            </AccordionSection>

            <AccordionSection title="2. Images" id="images" openSection={openSection} onToggle={onToggleSection}>
                <MediaManager formData={formData} onFormChange={onFormChange} />
            </AccordionSection>

            <AccordionSection title="3. Contact Information" id="contact" openSection={openSection} onToggle={onToggleSection}>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Sales Contact Name</label>
                        <input type="text" name="sales_contact_name" value={formData.sales_contact_name || ''} onChange={onFormChange} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Sales Contact Phone</label>
                        <input type="tel" name="sales_contact_phone" value={formData.sales_contact_phone || ''} onChange={onFormChange} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm" />
                    </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Sales Contact Email</label>
                        <input type="email" name="sales_contact_email" value={formData.sales_contact_email || ''} onChange={onFormChange} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Company Name & Address</label>
                        <input type="text" name="company_name_address" value={formData.company_name_address || ''} onChange={onFormChange} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm" />
                    </div>
                </div>
            </AccordionSection>
        </div>
    );
}

export default ConsumablesForm;
